import type { DiagnosisMap, DiagnosisMapResponse, OptionDiagnosis } from "@taskhealth/shared";
import { CONFIDENCE_RANK } from "@taskhealth/shared";

import { fetchDiagnosisMap } from "@/lib/api";

// Symptom→diagnosis matrix client cache.
//
// The map is static reference config (identical across visits) served by
// GET /diagnosis-map. We fetch it once per app session and keep it in module
// memory, so the on-device "Suggested based on your answers" group renders
// instantly with no per-tap network call. No AsyncStorage / native dep — a
// cold start re-fetches once, which is cheap (a few KB gzipped).

let cache: DiagnosisMapResponse | null = null;
let inFlight: Promise<DiagnosisMapResponse> | null = null;

/** Returns the option→diagnoses map, fetching+caching on first call. */
export async function loadDiagnosisMap(): Promise<DiagnosisMap> {
    if (cache) {
        return cache.map;
    }
    if (!inFlight) {
        inFlight = fetchDiagnosisMap()
            .then((res) => {
                cache = res;
                return res;
            })
            .finally(() => {
                inFlight = null;
            });
    }
    const res = await inFlight;
    return res.map;
}

/** Test/util hook — clears the in-memory cache. */
export function __resetDiagnosisMapCache(): void {
    cache = null;
    inFlight = null;
}

export interface AnswerBasedSuggestion extends OptionDiagnosis {
    /** ICD code is the dedupe + persist key (mirrors visit_diagnoses.icd_code). */
    icdCode: string;
}

/**
 * Derive the answer-based suggestion list for a section from the option ids the
 * RN has currently selected. Dedupes across symptoms (a code suggested by two
 * symptoms appears once, keeping its highest confidence), drops any code the RN
 * already has (profile or confirmed this visit — `excludeCodes`), and sorts
 * High→Medium→Low so the firmest suggestions lead.
 */
export function deriveAnswerBasedSuggestions(
    selectedOptionIds: number[],
    map: DiagnosisMap,
    excludeCodes: Set<string>
): AnswerBasedSuggestion[] {
    const byCode = new Map<string, AnswerBasedSuggestion>();
    for (const optionId of selectedOptionIds) {
        const entries = map[optionId];
        if (!entries) {
            continue;
        }
        for (const dx of entries) {
            // Only surface firm suggestions — Low-confidence (implied / inferred)
            // codes are kept in the data but never auto-presented, to avoid
            // nudging the RN toward over-coding.
            if (dx.confidence === "Low") {
                continue;
            }
            if (excludeCodes.has(dx.icdCode)) {
                continue;
            }
            const prev = byCode.get(dx.icdCode);
            // Keep the highest-confidence variant when two symptoms point to the
            // same code.
            if (!prev || CONFIDENCE_RANK[dx.confidence] < CONFIDENCE_RANK[prev.confidence]) {
                byCode.set(dx.icdCode, { ...dx });
            }
        }
    }
    return Array.from(byCode.values()).sort(
        (a, b) => CONFIDENCE_RANK[a.confidence] - CONFIDENCE_RANK[b.confidence]
    );
}
